'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import NewsletterForm from './NewsletterForm';
import { ABOUT_LINKS, HELP_LINKS, SHOP_CATEGORIES, SHOP_OCCASIONS, isPortalPath } from './nav';

export default function SiteFooter() {
  const pathname = usePathname();
  const year = new Date().getFullYear();

  if (isPortalPath(pathname)) {
    return (
      <footer className="border-t border-al-line bg-al-ink py-4 text-center font-al-body text-[11px] uppercase tracking-[0.2em] text-al-cream/50">
        © {year} Aarohi Luxe
      </footer>
    );
  }

  return (
    <footer className="al-surface border-t border-al-line/70">
      {/* Newsletter band */}
      <section className="relative overflow-hidden bg-al-ink-raised">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(226,185,127,0.12),transparent_60%)]" />
        <div className="al-container relative grid items-center gap-8 py-14 lg:grid-cols-2 lg:py-16">
          <div>
            <p className="al-eyebrow">The Aarohi Letter</p>
            <h2 className="al-heading mt-3 text-3xl sm:text-4xl">First look at every new weave.</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-al-cream/65">
              New collections, festive edits and stories from the ateliers we work with — a few times a month, never more.
            </p>
          </div>
          <NewsletterForm />
        </div>
      </section>

      <div className="al-zari" />

      <div className="al-container grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]">
        <div>
          <Link href="/" className="flex items-center gap-3" aria-label="Aarohi Luxe — home">
            <Image src="/brand/logo-mark.webp" alt="" width={48} height={48} className="h-12 w-12" />
            <span className="font-al-display text-3xl leading-none">
              <span className="text-al-gold">Aarohi</span> <span className="text-al-cream">Luxe</span>
            </span>
          </Link>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-al-cream/60">
            Heritage Indian fashion — hand-woven sarees, bridal lehengas, salwar suits and anarkalis from artisan ateliers across India.
          </p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-al-gold/80">Ships across the US · Prices in USD</p>
        </div>

        <FooterColumn title="Shop" links={SHOP_CATEGORIES.map((c) => ({ label: c.label, href: c.href }))} />
        <FooterColumn title="Occasions" links={SHOP_OCCASIONS.map((o) => ({ label: o.label, href: o.href }))} />
        <FooterColumn title="Help" links={[...HELP_LINKS]} />
        <FooterColumn title="Aarohi" links={[...ABOUT_LINKS, { label: 'My account', href: '/account' }]} />
      </div>

      <div className="border-t border-al-line/60">
        <div className="al-container flex flex-col items-center justify-between gap-3 py-6 text-[11px] uppercase tracking-[0.18em] text-al-cream/45 sm:flex-row">
          <p>© {year} Aarohi Luxe. All rights reserved.</p>
          <p>Visa · Mastercard · Amex · PayPal</p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }: { title: string; links: { label: string; href: string }[] }) {
  return (
    <div>
      <h3 className="al-eyebrow">{title}</h3>
      <ul className="mt-5 space-y-3 text-sm">
        {links.map((l) => (
          <li key={`${l.label}-${l.href}`}>
            <Link href={l.href} className="text-al-cream/70 transition-colors hover:text-al-gold">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
